import { Alert, Box, Button, Dialog, DialogActions, DialogTitle, Stack, Tooltip } from '@mui/material';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../../redux/ReduxHooks';
import { selectAreDrivebaseCommandActionsDisabled } from '../../../../redux/RobotSlice';
import { electronics, RobotActions } from '../../../../utils/constants';
import { DRIVE_UNIT_PORT_MAX, DRIVE_UNIT_PORT_MIN } from '../../../../utils/driveUnitPortSelection';
import { DeviceCardProps } from '../DeviceCardProps';
import DriveUnitPortControl from '../Controls/DriveUnitPortControl';
import InputControl from '../Controls/InputControl';
import DeviceCardWrapper from '../DeviceCardWrapper';
import { BeamBreakIcon } from '../../../Icons/Bits';
import { useDriveUnitPortRegistry } from '../../../../hooks/controls/useDriveUnitPortRegistry';
import { initializeParameter, updateParameter } from '../DeviceCardBuilder';
import { isPlainNumericLiteral } from '../Controls/unsupportedNumericValue';
import BeamBreakCheckDialog, {
  BEAM_BREAK_THRESHOLD_DECIMAL_PLACES,
  DEFAULT_BEAM_BREAK_THRESHOLD_M,
} from '../../../Dialogs/Robot/ElectronicCheckDialogs/BeamBreakCheckDialog';
import { clampThresholdMeters } from '../../../Dialogs/Robot/ElectronicCheckDialogs/distanceBasedSensorCheckUtils';
import { getMainProgramModel } from '../../utils/mainProgramEditor';

interface BeamBreakCheckState {
  port: number;
  initialThresholdMeters: number;
  sourceGeneration: number;
  sourceModel: ReturnType<typeof getMainProgramModel>;
  sourceVersionId: number | null;
}

interface BeamBreakCheckRequest extends BeamBreakCheckState {
  id: number;
}

type BeamBreakCheckWarning = 'noSensor' | 'requestFailed' | 'reconnectRequired' | null;

const REMOTE_DEVICE_TYPES_RECONNECT_REQUIRED_ERROR_CODE = 'REMOTE_DEVICE_TYPES_RECONNECT_REQUIRED';

const isSupportedDriveUnitPort = (port: number | undefined): port is number =>
  typeof port === 'number' && Number.isInteger(port) && port >= DRIVE_UNIT_PORT_MIN && port <= DRIVE_UNIT_PORT_MAX;

const getSourceVersionId = (model: ReturnType<typeof getMainProgramModel>): number | null => model?.getVersionId?.() ?? null;

const BeamBreakSensorCard = (props: DeviceCardProps) => {
  const { t } = useTranslation();
  const { portControlValue, setPortControlValue, unsupportedRawValue } = useDriveUnitPortRegistry(props, 'sensor_port');
  const [checkState, setCheckState] = useState<BeamBreakCheckState | null>(null);
  const [checkRequest, setCheckRequest] = useState<BeamBreakCheckRequest | null>(null);
  const [checkWarning, setCheckWarning] = useState<BeamBreakCheckWarning>(null);
  const checkRequestIdRef = useRef(0);
  const currentPortRef = useRef(portControlValue);
  const loadedProjectId = useAppSelector((state) => state.project?.loadedProject?.id ?? null);
  const programSourceRevision = useAppSelector((state) => state.project?.programSourceRevision ?? 0);
  const sourceEditor = props.editor ?? window.__bps_editor ?? null;
  const sourceModel = getMainProgramModel(sourceEditor);
  const sourceGenerationRef = useRef(0);
  const sourceIdentityRef = useRef({ loadedProjectId, programSourceRevision, sourceEditor, sourceModel });
  const isRobotConnected = useAppSelector((state) => state.robot.robotState === RobotActions.STATE.CONNECTED);
  const isRobotConnectedRef = useRef(isRobotConnected);
  const remoteDeviceTypesReady = useAppSelector((state) => state.robotData.remoteDeviceTypesReady);
  const checkedElectronicType = useAppSelector((state) => {
    if (checkState == null) return null;
    return state.robotData.electronics[checkState.port - 1]?.electronicType ?? null;
  });
  const commandActionsDisabled = useAppSelector(selectAreDrivebaseCommandActionsDisabled);
  const commandActionsDisabledRef = useRef(commandActionsDisabled);
  const isCurrentSourceSnapshot = (snapshot: BeamBreakCheckState) =>
    snapshot.sourceGeneration === sourceGenerationRef.current &&
    snapshot.sourceModel === getMainProgramModel(sourceEditor) &&
    snapshot.sourceVersionId === getSourceVersionId(snapshot.sourceModel);
  const checkedPortHasBeamBreakSensor =
    checkState != null &&
    isCurrentSourceSnapshot(checkState) &&
    isRobotConnected &&
    portControlValue === checkState.port &&
    remoteDeviceTypesReady &&
    checkedElectronicType === electronics.beamBreakSensor.ELECTRONIC_TYPE;
  const checkDisabled = !isRobotConnected || !isSupportedDriveUnitPort(portControlValue) || commandActionsDisabled || checkRequest != null;
  const checkUnavailableReason = !isRobotConnected
    ? t('beamBreakControl.checkUnavailable.notConnected', {
        defaultValue: 'Connect to a drive unit to check the sensor.',
      })
    : commandActionsDisabled
    ? t('programState.unavailableWhileRuntimeBusy', {
        defaultValue: 'Unavailable while the drive unit is busy',
      })
    : '';

  currentPortRef.current = portControlValue;
  isRobotConnectedRef.current = isRobotConnected;
  commandActionsDisabledRef.current = commandActionsDisabled;

  useLayoutEffect(() => {
    const sourceIdentity = sourceIdentityRef.current;
    if (
      sourceIdentity.loadedProjectId === loadedProjectId &&
      sourceIdentity.programSourceRevision === programSourceRevision &&
      sourceIdentity.sourceEditor === sourceEditor &&
      sourceIdentity.sourceModel === sourceModel
    ) {
      return;
    }

    sourceIdentityRef.current = { loadedProjectId, programSourceRevision, sourceEditor, sourceModel };
    sourceGenerationRef.current += 1;
    checkRequestIdRef.current += 1;
    setCheckRequest(null);
    setCheckState(null);
    setCheckWarning(null);
  }, [loadedProjectId, programSourceRevision, sourceEditor, sourceModel]);

  useEffect(() => {
    if (checkState != null && !checkedPortHasBeamBreakSensor) {
      setCheckState(null);
    }
  }, [checkState, checkedPortHasBeamBreakSensor]);

  useEffect(() => {
    if (sourceModel == null) return;

    const disposable = sourceModel.onDidChangeContent?.(() => {
      checkRequestIdRef.current += 1;
      setCheckRequest(null);
      setCheckState(null);
      setCheckWarning(null);
    });

    return () => disposable?.dispose();
  }, [sourceModel]);

  useEffect(() => {
    checkRequestIdRef.current += 1;
    setCheckRequest((currentRequest) => (currentRequest != null && currentRequest.port !== portControlValue ? null : currentRequest));
    setCheckWarning(null);
  }, [portControlValue]);

  useEffect(() => {
    if (!isRobotConnected) {
      checkRequestIdRef.current += 1;
      setCheckRequest(null);
      setCheckState(null);
      setCheckWarning(null);
    }
  }, [isRobotConnected]);

  useEffect(
    () => () => {
      checkRequestIdRef.current += 1;
    },
    []
  );

  const handleOpenCheck = () => {
    if (checkDisabled || !isSupportedDriveUnitPort(portControlValue)) return;

    const rawThreshold = initializeParameter({ ...props, parameterName: 'detection_range' });
    const thresholdMeters = isPlainNumericLiteral(rawThreshold) ? Number(rawThreshold) : DEFAULT_BEAM_BREAK_THRESHOLD_M;
    const requestId = checkRequestIdRef.current + 1;
    checkRequestIdRef.current = requestId;
    const request = {
      id: requestId,
      port: portControlValue,
      initialThresholdMeters: clampThresholdMeters(thresholdMeters, BEAM_BREAK_THRESHOLD_DECIMAL_PLACES),
      sourceGeneration: sourceGenerationRef.current,
      sourceModel,
      sourceVersionId: getSourceVersionId(sourceModel),
    };
    setCheckWarning(null);
    setCheckRequest(request);

    const refreshRequest = window.robotService?.getRemoteDeviceTypesOnce?.();
    if (!refreshRequest) {
      setCheckRequest(null);
      setCheckWarning('requestFailed');
      return;
    }

    void refreshRequest.then(
      (connectedPortTypes) => {
        if (
          checkRequestIdRef.current !== request.id ||
          currentPortRef.current !== request.port ||
          !isCurrentSourceSnapshot(request) ||
          !isRobotConnectedRef.current
        ) {
          return;
        }

        setCheckRequest(null);
        if (commandActionsDisabledRef.current) {
          return;
        }
        if (connectedPortTypes[request.port - 1] === electronics.beamBreakSensor.ELECTRONIC_TYPE) {
          setCheckState({
            port: request.port,
            initialThresholdMeters: request.initialThresholdMeters,
            sourceGeneration: request.sourceGeneration,
            sourceModel: request.sourceModel,
            sourceVersionId: request.sourceVersionId,
          });
          return;
        }
        setCheckWarning('noSensor');
      },
      (error: unknown) => {
        if (
          checkRequestIdRef.current !== request.id ||
          currentPortRef.current !== request.port ||
          !isCurrentSourceSnapshot(request) ||
          !isRobotConnectedRef.current
        )
          return;
        setCheckRequest(null);
        if (commandActionsDisabledRef.current) return;
        const errorCode = typeof error === 'object' && error != null && 'code' in error ? error.code : null;
        setCheckWarning(errorCode === REMOTE_DEVICE_TYPES_RECONNECT_REQUIRED_ERROR_CODE ? 'reconnectRequired' : 'requestFailed');
      }
    );
  };

  const handleUseCurrentThreshold = (thresholdMeters: number) => {
    if (checkState == null || !isCurrentSourceSnapshot(checkState)) return;

    const normalizedThreshold = clampThresholdMeters(thresholdMeters, BEAM_BREAK_THRESHOLD_DECIMAL_PLACES);
    updateParameter({ ...props, parameterName: 'detection_range' }, normalizedThreshold.toString(), { focusEditor: false });
  };

  return (
    <>
      <DeviceCardWrapper header={props.header} headerIcon={BeamBreakIcon} initialOpen={props.initialOpen} onDelete={props.onDelete}>
        <DriveUnitPortControl
          {...props}
          parameterName="sensor_port"
          portControlValue={portControlValue}
          setPortControlValue={setPortControlValue}
          unsupportedRawValue={unsupportedRawValue}
          label={<Trans i18nKey="sensor.port">Sensor Port</Trans>}
          labelId="sensor-port-label"
          labelPopoverId="sensor-port-popover"
          labelPopover={t('sensor.port_info_tooltip')}
          selectId="sensor-port"
          selectLabelId="select-sensor-port-label"
        />
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Box sx={{ flex: 1, '& .MuiFormControl-root': { width: '100%' } }}>
            <InputControl
              {...props}
              parameterName="detection_range"
              label={<Trans i18nKey="beamBreakControl.detection_range">Detection Threshold</Trans>}
              labelId="detection-range"
              labelPopoverId="detection-range-popover"
              labelPopover={t('beamBreakControl.detection_range_info_tooltip')}
              step={0.01}
              min={0}
              max={1}
              maxDecimalPlaces={3}
              unitLabel="m"
            />
          </Box>
          <Tooltip
            title={checkUnavailableReason}
            disableHoverListener={!checkDisabled}
            disableFocusListener={!checkDisabled}
            disableTouchListener={!checkDisabled}
          >
            <Box component="span" sx={{ pt: 1 }}>
              <Button
                startIcon={
                  <Box
                    component={BeamBreakIcon}
                    aria-hidden
                    sx={{
                      width: 20,
                      height: 20,
                      display: 'block',
                      color: 'inherit',
                    }}
                  />
                }
                onClick={handleOpenCheck}
                disabled={checkDisabled}
              >
                <Trans i18nKey="beamBreakControl.button.check">Check</Trans>
              </Button>
            </Box>
          </Tooltip>
        </Stack>
        {checkWarning === 'requestFailed' && (
          <Alert severity="warning" onClose={() => setCheckWarning(null)} closeText={t('button.close', { defaultValue: 'Close' })}>
            <Trans i18nKey="beamBreakControl.checkWarning.requestFailed">Unable to check the sensor. Try again.</Trans>
          </Alert>
        )}
      </DeviceCardWrapper>
      <Dialog open={checkWarning === 'noSensor'} onClose={() => setCheckWarning(null)} maxWidth="sm">
        <DialogTitle>
          <Trans i18nKey="beamBreakControl.checkWarning.noSensor">No beam break sensor is connected to the selected port.</Trans>
        </DialogTitle>
        <DialogActions>
          <Button onClick={() => setCheckWarning(null)} variant="contained">
            <Trans i18nKey="button.close">Close</Trans>
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={checkWarning === 'reconnectRequired'} onClose={() => setCheckWarning(null)} maxWidth="sm">
        <DialogTitle>
          <Trans i18nKey="beamBreakControl.checkWarning.reconnectRequired">
            Reconnect the drive unit before checking connected electronics again.
          </Trans>
        </DialogTitle>
        <DialogActions>
          <Button onClick={() => setCheckWarning(null)} variant="contained">
            <Trans i18nKey="button.close">Close</Trans>
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={checkedPortHasBeamBreakSensor} onClose={() => setCheckState(null)} maxWidth="md">
        {checkState != null && checkedPortHasBeamBreakSensor && (
          <BeamBreakCheckDialog
            open
            port={checkState.port}
            initialThresholdMeters={checkState.initialThresholdMeters}
            onUseCurrentThreshold={handleUseCurrentThreshold}
            onClose={() => setCheckState(null)}
          />
        )}
      </Dialog>
    </>
  );
};

export default BeamBreakSensorCard;
